import {gql} from '@apollo/client';
import {Tooltip} from '@mantine/core';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {SeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {ListableItem} from 'search/SearchConstants';
import {StudySearchItem} from 'studies/StudySearchInput';
import {
  getCannotCreateAnalysisMessageForStudyType,
  getCommonStudyType,
  getInitialAnalysisNameForCrossStudyAnalysis,
  getTooManyStudiesMessage,
  NO_PROCEDURE_RUNS_TO_ANALYZE_MESSAGE,
} from 'studies/typed/utils/analysis/analysisUtils';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import ProcedureBasedStudyAnalysisModalBanner from 'studies/typed/utils/analysis/ProcedureBasedStudyAnalysisModalBanner';
import {getProcedureTypeForStudyType, getStudyTypeText} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';

import {StudyListingGraphQL} from '../../../graphql-fragments/studies/__generated/StudyListingGraphQL';
import {
  CrossStudyAnalysisRunCountsQuery,
  CrossStudyAnalysisRunCountsQueryVariables,
} from './__generated/CrossStudyAnalysisRunCountsQuery';

/**
 * Study types that can be analyzed from the Studies listing page. Other study types (Clinical,
 * Preclinical, Custom) are not supported for cross-study analysis at this time.
 */
const CROSS_STUDY_ANALYSIS_SUPPORTED_STUDY_TYPES: ReadonlySet<StudyType> = new Set([
  StudyType.PROCESS_DEVELOPMENT,
  StudyType.ASSAY_DEVELOPMENT,
]);

export const NO_STUDIES_SELECTED_MESSAGE = 'Select one or more studies to analyze.';
export const RUN_DATA_LOAD_ERROR_MESSAGE = 'Failed to load run data. Refresh the page and try again.';
export const MIXED_STUDY_TYPES_MESSAGE =
  'This button is disabled because you have selected multiple types of studies';
export function getUnsupportedStudyTypeMessage(studyType: StudyType): string {
  return `${getStudyTypeText(studyType)} studies are not supported for analysis from this listing page.`;
}

/**
 * Fetches the accessible procedure runs for each selected study. Used to determine whether the
 * Analyze button should be enabled. The button is disabled when there are no accessible procedure
 * runs across all selected studies.
 *
 * `procedureRuns` applies per-run READ access filtering (returning `InaccessibleInfoNode` for runs
 * the user cannot see), so the count correctly reflects what the user can actually analyze.
 */
export const CROSS_STUDY_ANALYSIS_RUN_COUNTS_QUERY = gql`
  query CrossStudyAnalysisRunCountsQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: STUDIES) {
    studies(ids: $ids) {
      ... on StudyNode {
        id
        procedureRuns {
          ... on ProcedureRunNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Return the selected `StudyListingGraphQL` items, given all items displayed in the listing and the ids
 * that the user has selected.
 */
export function getSelectedStudies(
  items: Array<ListableItem> | null,
  selectedIds: Array<string>
): Array<StudyListingGraphQL> {
  if (!items) {
    return [];
  }
  const selectedIdSet = new Set(selectedIds);
  const studies: Array<StudyListingGraphQL & ListableItem> = [];
  for (const item of items) {
    // Items in the Studies listing are always `StudyListingGraphQL` instances (the listing is configured
    // with `availableTypes: [ModelTypeConstants.STUDY]`); this id check is defensive.
    if (APIUtil.isStudy(item.id) && selectedIdSet.has(item.id)) {
      studies.push(item as StudyListingGraphQL & ListableItem);
    }
  }
  return studies;
}

/**
 * Whether the current user has permission to create cross-study analyses, based on their recipe/assay
 * capabilities. The user must have the `CREATE_ANALYSIS` feature for at least one of `RECIPE` or `ASSAY`.
 *
 * The `ASSAY` branch is additionally gated on `PROCEDURES_ENABLE_ASSAYS` so that tenants with the assay
 * capability but the assay-development feature flag off do not see the button.
 */
export function hasCreateAnalysisCapability(): boolean {
  return (
    isFeatureEnabledForProcedureType(ProcedureType.RECIPE, ProceduresFeature.CREATE_ANALYSIS) ||
    (BenchlingConfig.PROCEDURES_ENABLE_ASSAYS &&
      isFeatureEnabledForProcedureType(ProcedureType.ASSAY, ProceduresFeature.CREATE_ANALYSIS))
  );
}

/**
 * Returns the tooltip text to show when the Analyze button is disabled, or `null` when the selection is
 * valid and the button should be enabled.
 */
export function getDisabledTooltipText(
  selectedStudies: Array<StudyListingGraphQL>,
  studiesPerAnalysisLimit: number,
  totalProcedureRunCount: number,
  isLoading: boolean,
  hasQueryError: boolean
): string | null {
  if (isLoading) {
    return 'Loading...';
  }
  if (hasQueryError) {
    return RUN_DATA_LOAD_ERROR_MESSAGE;
  }
  if (selectedStudies.length === 0) {
    return NO_STUDIES_SELECTED_MESSAGE;
  }
  if (selectedStudies.length > studiesPerAnalysisLimit) {
    return getTooManyStudiesMessage(studiesPerAnalysisLimit, selectedStudies.length);
  }
  const commonStudyType = getCommonStudyType(selectedStudies.map((study) => study.schema.studyType));
  if (commonStudyType == null) {
    return MIXED_STUDY_TYPES_MESSAGE;
  }
  if (!CROSS_STUDY_ANALYSIS_SUPPORTED_STUDY_TYPES.has(commonStudyType)) {
    return getUnsupportedStudyTypeMessage(commonStudyType);
  }

  if (
    !isFeatureEnabledForProcedureType(
      getProcedureTypeForStudyType(commonStudyType),
      ProceduresFeature.CREATE_ANALYSIS
    )
  ) {
    return getCannotCreateAnalysisMessageForStudyType(commonStudyType);
  }

  if (totalProcedureRunCount === 0) {
    return NO_PROCEDURE_RUNS_TO_ANALYZE_MESSAGE;
  }
  return null;
}

function getInitialSeedDataConfig(studyIds: Array<string>): SeedDataConfig {
  assert(studyIds.length > 0, 'Expected at least one study to be selected.');
  return {type: SeedDataConfigType.STUDY, data: {studyIds}};
}

export interface CreateCrossStudyAnalysisButtonProps {
  selectedStudies: Array<StudyListingGraphQL>;
  actionOrigin: ActionOrigin;
}

interface CreateCrossStudyAnalysisButtonPresentationalProps extends CreateCrossStudyAnalysisButtonProps {
  totalProcedureRunCount: number;
  isLoading: boolean;
  hasQueryError: boolean;
}

function CreateCrossStudyAnalysisButtonPresentational(
  props: CreateCrossStudyAnalysisButtonPresentationalProps
): JSX.Element {
  const {selectedStudies, actionOrigin, totalProcedureRunCount, isLoading, hasQueryError} = props;
  const tooltipText = getDisabledTooltipText(
    selectedStudies,
    BenchlingConfig.STUDIES_PER_CROSS_STUDY_ANALYSIS_LIMIT,
    totalProcedureRunCount,
    isLoading,
    hasQueryError
  );
  const buttonDisabled = tooltipText != null;
  const initialStudySearchItems: Array<StudySearchItem> = selectedStudies.map((study) => ({
    id: study.id,
    name: study.name,
    humanId: study.humanId,
  }));
  const studyType = getCommonStudyType(selectedStudies.map((study) => study.schema.studyType));
  const title = selectedStudies.length === 1 ? 'Study analysis' : 'Cross-study analysis';

  const handleClick = async (): Promise<void> => {
    assert(studyType != null, 'Button should be disabled if no common study type is found.');

    const studyIds = selectedStudies.map((study) => study.id);
    const initialSeedDataConfig = getInitialSeedDataConfig(studyIds);
    const procedureType = getProcedureTypeForStudyType(studyType);
    await legacyRunModal(CreateAnalysisModal, {
      actionOrigin,
      onCreateAnalysis: (data: CreateAnalysisParams) =>
        createAnalysisForStudy(data, {
          actionOrigin,
          studyType,
          procedureType,
          sourceObjectIds: studyIds,
        }),
      initialStudySearchItems,
      disableEditStudies: true,
      title,
      initialAnalysisName: () => getInitialAnalysisNameForCrossStudyAnalysis(selectedStudies),
      headerInlineBanner: <ProcedureBasedStudyAnalysisModalBanner studyType={studyType} />,
      hideDataSharingWarningBanner: true,
      initialSeedDataConfig,
      showStudiesField: selectedStudies.length === 1,
    });
  };

  return (
    <Tooltip label={tooltipText ?? ''} disabled={!buttonDisabled}>
      {/* Mantine Tooltip does not fire on disabled elements; the span captures pointer events instead */}
      <span>
        <Button
          dataTestAttrs={{component: 'CreateCrossStudyAnalysisButton', element: 'root'}}
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          text="Analyze"
          icon={ModelTypeConstants.ANALYSIS}
          onClick={handleClick}
          disabled={buttonDisabled}
        />
      </span>
    </Tooltip>
  );
}

export default function CreateCrossStudyAnalysisButton(
  props: CreateCrossStudyAnalysisButtonProps
): JSX.Element {
  const {selectedStudies} = props;

  const runCountsResult = useSkippableBenchlingQuery<
    CrossStudyAnalysisRunCountsQuery,
    CrossStudyAnalysisRunCountsQueryVariables
  >(CROSS_STUDY_ANALYSIS_RUN_COUNTS_QUERY, {
    skip: selectedStudies.length === 0,
    variables: {ids: selectedStudies.map((s) => s.id)},
    fetchPolicy: 'cache-and-network',
  });

  const isLoading = selectedStudies.length > 0 && runCountsResult.type === QueryResultType.PENDING;
  const hasQueryError = runCountsResult.type === QueryResultType.ERROR;

  const totalProcedureRunCount =
    runCountsResult.type === QueryResultType.SUCCESS
      ? accessibleObjs(runCountsResult.data.studies).reduce(
          (sum, study) => sum + accessibleObjs(study.procedureRuns).length,
          0
        )
      : 0;

  return (
    <CreateCrossStudyAnalysisButtonPresentational
      {...props}
      totalProcedureRunCount={totalProcedureRunCount}
      isLoading={isLoading}
      hasQueryError={hasQueryError}
    />
  );
}
